import React, { createContext, useContext, useState, useCallback, type ComponentType } from 'react';

import type { InjectedModalProps, ModalContextType } from './types';

export const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  // Internal state needs to handle the component and props as a unified block
  const [modal, setModal] = useState<{
    Component: ComponentType<InjectedModalProps>;
    props: Record<string, unknown>;
    isOpen: boolean;
  } | null>(null);

  const openModal = useCallback(
    <T extends InjectedModalProps>(Component: ComponentType<T>, props: Omit<T, keyof InjectedModalProps>) => {
      setModal({
        // We cast to a base version of the component that accepts injected props
        Component: Component as ComponentType<InjectedModalProps>,
        props: props as Record<string, unknown>,
        isOpen: true,
      });
    },
    []
  );

  const closeModal = useCallback(() => {
    setModal((prev) => (prev ? { ...prev, isOpen: false } : null));
  }, []);

  const handleAnimationEnd = useCallback(() => {
    if (modal && !modal.isOpen) {
      setModal(null); // Physical unmount after fadeOut
    }
  }, [modal]);

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {modal && (
        <modal.Component
          {...modal.props}
          isOpen={modal.isOpen}
          onCloseModal={closeModal}
          onAnimationEnd={handleAnimationEnd}
        />
      )}
    </ModalContext.Provider>
  );
}

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) throw new Error('useModal must be used within ModalProvider');
  return context;
};
